import Vue from 'vue'
import VueRouter from 'vue-router'
import { Message } from 'element-ui'
import { getCurrentUser } from '@/api/auth'
import {
  AUDIO_PHASE1_DEPRECATED_ROUTES,
  getAudioPhase1FallbackRoute,
  isAudioPhase1DeprecatedRoute
} from '@/constants/audioPhase1'

Vue.use(VueRouter)

const DeprecatedAudioRoutePlaceholder = {
  render(createElement) {
    return createElement()
  }
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    component: () => import('@/layout/Index.vue'),
    redirect: '/statistics',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'admin',
        name: 'Admin',
        component: () => import('@/views/admin/AdminList.vue'),
        meta: { title: '管理员管理' }
      },
      {
        path: 'role',
        name: 'Role',
        component: () => import('@/views/role/RoleList.vue'),
        meta: { title: '角色管理' }
      },
      {
        path: 'permission',
        name: 'Permission',
        component: () => import('@/views/permission/PermissionList.vue'),
        meta: { title: '权限管理' }
      },
      {
        path: 'user',
        name: 'User',
        component: () => import('@/views/user/UserList.vue'),
        meta: { title: '用户管理' }
      },
      {
        path: 'order',
        name: 'Order',
        component: () => import('@/views/order/OrderList.vue'),
        meta: { title: '订单管理' }
      },
      {
        path: 'order-config',
        name: 'OrderConfig',
        component: () => import('@/views/order/OrderConfig.vue'),
        meta: { title: '订阅配置' }
      },
      {
        path: 'audio',
        name: 'Audio',
        component: () => import('@/views/audio/AudioList.vue'),
        meta: { title: '音频管理' }
      },
      {
        path: 'audio-favorite',
        name: 'AudioFavorite',
        component: () => import('@/views/audio/AudioFavorite.vue'),
        meta: { title: '用户收藏音频' }
      },
      {
        path: 'audio-log',
        name: 'AudioLog',
        component: () => import('@/views/audio/AudioLog.vue'),
        meta: { title: '用户生成音频日志' }
      },
      ...AUDIO_PHASE1_DEPRECATED_ROUTES.map(route => ({
        path: route.path,
        name: route.name,
        component: DeprecatedAudioRoutePlaceholder,
        meta: { title: route.title }
      })),
      {
        path: 'notification',
        name: 'Notification',
        component: () => import('@/views/notification/NotificationList.vue'),
        meta: { title: '通知管理' }
      },
      {
        path: 'legal-content',
        name: 'LegalContent',
        component: () => import('@/views/content/LegalContent.vue'),
        meta: { title: '协议/FAQ内容' }
      },
      {
        path: 'statistics',
        name: 'Statistics',
        component: () => import('@/views/statistics/Statistics.vue'),
        meta: { title: '数据统计' }
      },
      {
        path: 'config',
        name: 'Config',
        component: () => import('@/views/config/Config.vue'),
        meta: { title: '公共配置' }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes
})

const routeNames = new Set([
  'Login',
  'Admin',
  'Role',
  'Permission',
  'User',
  'Order',
  'OrderConfig',
  'Audio',
  'AudioFavorite',
  'AudioLog',
  'Notification',
  'LegalContent',
  'Statistics',
  'Config'
])

function getFirstAccessibleRoute(permissions) {
  if (!Array.isArray(permissions)) {
    return '/'
  }

  const routeName = permissions.find(code => {
    return routeNames.has(code) && !isAudioPhase1DeprecatedRoute(code)
  })

  if (routeName) {
    return { name: routeName }
  }

  return getAudioPhase1FallbackRoute(permissions)
}

router.beforeEach(async (to, from, next) => {
  const accessToken = localStorage.getItem('accessToken')
  const requiresAuth = to.matched.some(
    record => record.meta && record.meta.requiresAuth
  )

  // 需要登录但没有 token，跳转到登录页
  if (requiresAuth && !accessToken) {
    return next('/login')
  }

  // 已经有 token 还访问登录页，跳转到第一个有权限的路由（没有则回首页）
  if (to.path === '/login') {
    if (accessToken) {
      let firstPermRoute = '/'
      try {
        const permStr = localStorage.getItem('permissions')
        if (permStr) {
          const perms = JSON.parse(permStr)
          firstPermRoute = getFirstAccessibleRoute(perms)
        }
      } catch (e) {
        // 解析失败则退回首页
        firstPermRoute = '/'
      }
      return next(firstPermRoute)
    }
    return next()
  }

  // 非登录页，且存在 token
  if (accessToken) {
    let permissions = []
    try {
      const userInfoStr = localStorage.getItem('userInfo')
      const permissionsStr = localStorage.getItem('permissions')

      // 本地没有用户信息或权限时，尝试获取一次
      if (!userInfoStr || !permissionsStr) {
        const user = await getCurrentUser()
        localStorage.setItem('userInfo', JSON.stringify(user))
        localStorage.setItem(
          'permissions',
          JSON.stringify(user.permissions || [])
        )
        window.dispatchEvent(new Event('permissions-updated'))
        permissions = user.permissions || []
      } else {
        permissions = JSON.parse(permissionsStr || '[]')
      }
    } catch (error) {
      // token 失效或其他错误，清理本地缓存并回到登录页
      localStorage.removeItem('accessToken')
      localStorage.removeItem('userInfo')
      localStorage.removeItem('permissions')
      window.dispatchEvent(new Event('permissions-updated'))
      return next('/login')
    }

    if (to.name && isAudioPhase1DeprecatedRoute(to.name)) {
      return next(getAudioPhase1FallbackRoute(permissions))
    }

    // 权限校验：login 之外的路由都需要在 permissions 中存在对应名称
    if (to.name && to.name !== 'Login') {
      const hasPermission = Array.isArray(permissions)
        ? permissions.includes(to.name)
        : false

      if (!hasPermission) {
        Message.error('暂无权限访问该页面')
        return next(false)
      }
    }
  }

  return next()
})

export default router
